import { Link } from "react-router-dom";
import { useCopy, useFramework } from "../framework";
import { iconFor } from "../components/icons";
import { Glyph, type GlyphMeaning } from "@apf/design-system/glyphs";
import { OfficeProductCards } from "../components/OfficeProductCards";
import { Door, PageHeader, PlaceLine, SectionHeading, Trail, ordinal, placePart } from "../components/ui";
import { useScrollToHash } from "./support";
import { domainWorkblockPath } from "../catalogue";
import { partnerCapabilityLinksForTarget } from "../capabilityMapping";
import { CapabilityMapping } from "../components/CapabilityMapping";

/**
 * Each rail carries its own copy pair: the description key the pack already
 * ships, and the same key suffixed `.label` for the term beside it. The English
 * term stays here only as the fallback the copy layer uses when a pack omits
 * the label.
 */
const railsProperties: Array<[string, string]> = [
  ["Durable runs", "architecture.property.durable"],
  ["One data plane", "architecture.property.data-plane"],
  ["Tenant learning", "architecture.property.learning"],
  ["Authority ladder", "architecture.property.autonomy"],
  ["Gated deploys", "architecture.property.deploys"]
];

function versionLabel(template: string, version: string): string {
  return template.replaceAll("{version}", version);
}

function replaceValues(template: string, values: Record<string, string | number>): string {
  return Object.entries(values).reduce(
    (resolved, [key, value]) => resolved.replaceAll(`{${key}}`, String(value)),
    template
  );
}

function valueAtPath(root: unknown, path: string): unknown {
  return path.split(".").reduce<unknown>((value, segment) => {
    if (typeof value !== "object" || value === null || !Object.hasOwn(value, segment)) return undefined;
    return (value as Record<string, unknown>)[segment];
  }, root);
}

function countLabel(snapshot: unknown, source: string, template: string): string | undefined {
  const value = valueAtPath(snapshot, source);
  if (!Array.isArray(value)) return undefined;
  return template.replaceAll("{count}", String(value.length));
}

/**
 * The coexistence story, carried by the Platform page rather than by a page of
 * its own: a reader asking what the platform is built from is the same reader
 * asking what it has to live beside. /landscape still resolves here.
 */
function RunsAlongside() {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const domainById = new Map(snapshot.content.domainWorkblocks.map((domain) => [domain.id, domain]));
  const domainCount = snapshot.content.domainWorkblocks.length;

  return (
    <section className="apf-page-section apf-runs-alongside" id="runs-alongside" aria-labelledby="runs-alongside-title">
      <div className="apf-section-heading">
        <div>
          <h2 id="runs-alongside-title">
            <Glyph meaning="class.coexistence" size={20} className="apf-heading-glyph" />
            {copy("page.platform.runs-alongside.heading", "Runs alongside what you already own")}
          </h2>
          <p>{copy("page.landscape.description")}</p>
        </div>
      </div>
      <div className="apf-coexistence-list">
        {snapshot.content.landscapes.map((entry) => (
          <article className="apf-coexistence" id={entry.id} key={entry.id}>
            <div className="apf-coexistence__body">
              <h3>{copy(entry.labelKey)}</h3>
              <p>{copy(entry.descriptionKey)}</p>
            </div>
            <div className="apf-coexistence__domains">
              <span className="apf-coexistence__domains-label">{copy("page.platform.runs-alongside.where-label", "Where it applies")}</span>
              {entry.domainWorkblockIds.length === domainCount ? (
                <Link to="/workblocks">{replaceValues(copy("page.platform.runs-alongside.all-workblocks", "All {count} Workblocks"), { count: domainCount })}</Link>
              ) : (
                entry.domainWorkblockIds.map((domainId) => {
                  const domain = domainById.get(domainId);
                  return domain ? <Link to={domainWorkblockPath(domain.id)} key={domain.id}>{copy(domain.shortLabelKey)}</Link> : null;
                })
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export function ArchitecturePage() {
  const { snapshot } = useFramework();
  const copy = useCopy();
  useScrollToHash();
  const domains = [...snapshot.content.domainWorkblocks].sort((a, b) => a.order - b.order);

  return (
    <div className="apf-page apf-register-reference">
      <PageHeader eyebrow={copy("page.architecture.eyebrow")} title={copy("page.architecture.title")} description={copy("page.architecture.description")} />
      {/* Where the reader is standing: the section word, what is in scope and
        * the framework cut. Every part resolves through the copy layer and
        * nowhere else, so a part the snapshot does not carry is dropped rather
        * than written into the page, where a count or a version goes stale
        * without saying so. */}
      <PlaceLine
        section={placePart(copy, "place.platform.section")}
        segments={[{ text: placePart(copy, "place.platform.scope") }]}
        cut={versionLabel(copy("catalogue.framework-version", "Framework {version}"), snapshot.frameworkVersion)}
      />

      <section className="apf-arch" aria-label={copy("page.architecture.structure-label", "Platform structure")}>
        <div className="apf-arch__layer">
          <div className="apf-arch__layer-name">
            <h2>{replaceValues(copy("page.architecture.domains.heading", "{count} domain Workblocks"), { count: domains.length })}</h2>
            <p>{copy("page.architecture.domains.description", "Each Workblock is a coherent system of work composed from five independently versioned business modules.")}</p>
          </div>
          <ul className="apf-arch__domains">
            {domains.map((domain) => (
              <li key={domain.id}>
                <Link to={domainWorkblockPath(domain.id)}>
                  <span>{ordinal(domain.order)}</span>
                  {copy(domain.shortLabelKey)}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="apf-arch__layer">
          <div className="apf-arch__layer-name">
            <h2>{copy("page.architecture.capabilities.heading", "Shared capabilities")}</h2>
            <p>{copy("page.architecture.capabilities.description", "Every authorised Workblock draws on the same data, identity, agent, Office and integration services, so evidence and audit stay in one place.")}</p>
          </div>
          <ol className="apf-ruled-index apf-ruled-index--capabilities">
            {snapshot.content.capabilities.map((capability, index) => {
              const Icon = iconFor(capability.icon);
              const capabilityMappings = partnerCapabilityLinksForTarget(snapshot, copy, "shared-capability", capability.id);
              return (
                <li className="apf-ruled-index__row" id={`shared-capability-${capability.id}`} key={capability.id}>
                  <div className="apf-ruled-index__body">
                    <span className="apf-ruled-index__ordinal">{ordinal(index + 1)}</span>
                    <div className="apf-ruled-index__heading">
                      <Icon size={20} />
                      {capability.id === "office-composer-conductor"
                        ? <strong className="apf-ruled-index__name"><Link to="/capabilities">{copy(capability.labelKey)}</Link></strong>
                        : <strong className="apf-ruled-index__name">{copy(capability.labelKey)}</strong>}
                    </div>
                    <p className="apf-ruled-index__description">{copy(capability.descriptionKey)}</p>
                    <CapabilityMapping
                      label={copy("partner-capability.detail.heading")}
                      items={capabilityMappings}
                      compact
                    />
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        <div className="apf-arch__layer apf-arch__foundation">
          <div className="apf-arch__layer-name">
            <h2>{copy("page.architecture.rails.heading", "Beyond Work execution rails")}</h2>
            <p>{copy("page.architecture.rails.description", "The foundation every Workblock runs on. The same properties hold across all nine domains.")}</p>
          </div>
          <div>
            <dl className="apf-arch__properties">
              {railsProperties.map(([label, key]) => (
                <div className="apf-arch__property" key={key}>
                  <dt>{copy(`${key}.label`, label)}</dt>
                  <dd>{copy(key)}</dd>
                </div>
              ))}
            </dl>
            <p className="apf-arch__trust">{copy("claim.trust")}</p>
          </div>
        </div>
      </section>

      <section className="apf-page-section">
        <SectionHeading title={copy("office.catalogue.section-heading")} description={copy("office.catalogue.section-description")} />
        <OfficeProductCards />
      </section>

      <RunsAlongside />

      {/* The two destinations a reader of the platform structure asks for next:
        * what runs on it, and what it writes to. One onward anatomy, the same
        * one every other page closes with. */}
      <Trail
        label={copy("trail.label", "Where to go next")}
        items={[
          { to: "/workblocks", glyph: "object.workblock", label: copy("trail.work"), context: copy("trail.work.context") },
          { to: "/data-model", glyph: "class.record", label: copy("trail.data"), context: copy("trail.data.context") }
        ]}
      />
    </div>
  );
}

/**
 * The Reference landing. Three destinations that a reader consults rather than
 * reads: the data model, the words, and how change ships. Keeping them one click
 * from the header keeps the header at seven items.
 *
 * The three sit in one hairline container as ruled rows, the anatomy every set
 * of destinations on the site now uses. They were three bordered boxes, each
 * ending in its own accent-coloured action label: three boxes flatten three
 * different destinations, and a repeated purple label is anchor inflation when
 * the row is already the target.
 */
export function ReferencePage() {
  const { snapshot } = useFramework();
  const copy = useCopy();
  useScrollToHash();
  // Membership, order, description, glyph and count source travel with each
  // projected route. A new reference destination therefore needs no page code.
  const destinations = snapshot.content.routes
    .filter((route) => route.referenceNavigation)
    .sort((left, right) => left.referenceNavigation!.order - right.referenceNavigation!.order);

  return (
    <div className="apf-page apf-register-reference apf-reference-page">
      <PageHeader
        eyebrow={copy("page.reference.eyebrow", "Reference")}
        title={copy("page.reference.title", "The record behind the framework.")}
        description={copy("page.reference.description", "The data model every Workblock writes to, the words the platform uses, and how a change reaches your platform.")}
      />
      <div className="apf-door-list">
        {destinations.map((route, index) => {
          const reference = route.referenceNavigation!;
          const meta = reference.count
            ? countLabel(snapshot, reference.count.source, copy(reference.count.labelKey))
            : undefined;
          return (
          <Door
            key={route.id}
            to={route.path}
            glyph={reference.glyphMeaning as GlyphMeaning}
            index={ordinal(index + 1)}
            title={copy(route.labelKey)}
            line={copy(reference.descriptionKey)}
            meta={meta}
          />
          );
        })}
      </div>
    </div>
  );
}

/**
 * The questions page. It is projected under the Reference section, so it wears
 * the reference register alongside its peer catalogues rather than the
 * marketing one, and it stays a ruled reading list: a 2px rule opens each
 * category, a hairline closes each question, and no question sits in a box of
 * its own. It declares no statement plane, as a reading surface does not
 * (packages/design-system/DESIGN.md).
 */
export function FaqsPage() {
  const { snapshot } = useFramework();
  const copy = useCopy();
  useScrollToHash();
  const groups = new Map<string, typeof snapshot.content.faqs>();
  for (const faq of snapshot.content.faqs) groups.set(faq.category, [...(groups.get(faq.category) ?? []), faq]);

  return (
    <div className="apf-page apf-register-reference">
      <PageHeader eyebrow={copy("page.faqs.eyebrow")} title={copy("page.faqs.title")} description={copy("page.faqs.description")} />
      {[...groups.entries()].map(([category, faqs]) => (
        <section className="apf-page-section" key={category}>
          <SectionHeading title={category} />
          <div className="apf-faq-list">
            {faqs.map((faq) => (
              <details className="apf-faq" id={faq.id} key={faq.id}>
                <summary>{copy(faq.questionKey)}</summary>
                <div className="apf-faq__answer">{copy(faq.answerKey)}</div>
              </details>
            ))}
          </div>
        </section>
      ))}
      <Trail
        label={copy("trail.label", "Where to go next")}
        items={[
          { to: "/language", glyph: "utility.glossary", label: copy("trail.language"), context: copy("trail.language.context") },
          { to: "/reference", glyph: "class.record", label: copy("trail.reference"), context: copy("trail.reference.context") }
        ]}
      />
    </div>
  );
}

export function ReleasePage() {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const release = snapshot.release;

  return (
    <div className="apf-page apf-register-reference apf-release-page">
      <PageHeader eyebrow={copy("page.release.eyebrow")} title={copy("page.release.title")} description={copy("page.release.description")} />
      {/* The section word, then the framework cut where the copy pack carries
        * one. The section falls back to the eyebrow this page already ships,
        * so the line never renders a key name and never invents a heading. */}
      <PlaceLine
        section={placePart(copy, "place.release.section") || copy("page.release.eyebrow")}
        cut={versionLabel(copy("catalogue.partner-version", "Partner release {version}"), snapshot.partnerVersion)}
      />

      {/* The public Release record, generated from the reviewed migration:
        * the exact current version set, its compatible predecessor, the
        * breaking-change boundary and the rollback posture. Internal release
        * operations never project here. */}
      {release ? (
        <section className="apf-page-section apf-release-record" aria-labelledby="release-record-title">
          <SectionHeading id="release-record-title" title={copy("page.release.record.title", "The current release")} />
          <dl className="apf-recipe-facts">
            <div><dt>{copy("page.release.record.framework-label", "Framework")}</dt><dd>{release.frameworkVersion}</dd></div>
            {release.gwodVersion ? <div><dt>{copy("page.release.record.gwod-label", "GWOD data model")}</dt><dd>{release.gwodVersion}</dd></div> : null}
            <div><dt>{copy("page.release.record.groundworks-label", "GroundWorks records")}</dt><dd>{release.groundworksVersion}</dd></div>
            <div><dt>{copy("page.release.record.modules-label", "Business modules")}</dt><dd>{release.businessModuleVersion}</dd></div>
            <div>
              <dt>{copy("page.release.record.predecessor-label", "Compatible predecessor")}</dt>
              <dd>
                {replaceValues(
                  copy("page.release.record.predecessor-value", "Framework {framework} · GroundWorks {groundworks} · business modules {modules}"),
                  {
                    framework: release.compatiblePredecessor.frameworkVersion,
                    groundworks: release.compatiblePredecessor.groundworksVersion,
                    modules: release.compatiblePredecessor.businessModuleVersion
                  }
                )}
              </dd>
            </div>
            <div><dt>{copy("page.release.record.downloads-label", "Schemas and manifests")}</dt><dd><Link to="/downloads">{copy("page.release.record.downloads-link", "Browse the downloadable schemas and manifests")}</Link></dd></div>
          </dl>
          <div className="apf-release-record__breaking">
            <h3>{copy("page.release.record.breaking-title", "The breaking-change boundary")}</h3>
            <p>{copy("page.release.record.breaking-description")}</p>
            <details>
              <summary>{copy("page.release.record.entity-renames-label", "Renamed records")} ({release.breakingChanges.entityRenames.length})</summary>
              <ul>
                {release.breakingChanges.entityRenames.map((rename) => (
                  <li key={`${rename.from}-${rename.to}`}><code>{rename.from}</code> → <code>{rename.to}</code></li>
                ))}
              </ul>
            </details>
            <details>
              <summary>{copy("page.release.record.field-renames-label", "Renamed fields")} ({release.breakingChanges.fieldRenames.length})</summary>
              <ul>
                {release.breakingChanges.fieldRenames.map((rename) => (
                  <li key={`${rename.entityId}-${rename.from}`}><code>{rename.entityId}</code>: <code>{rename.from}</code> → <code>{rename.to}</code></li>
                ))}
              </ul>
            </details>
          </div>
          <div className="apf-release-record__rollback">
            <h3>{copy("page.release.record.rollback-title", "Rollback posture")}</h3>
            <p>{copy("page.release.record.rollback-body")}</p>
          </div>
        </section>
      ) : null}
      <div className="apf-ruled-columns">
        <article className="apf-ruled-columns__item">
          <p className="apf-ruled-columns__eyebrow">{copy("page.release.versioned.eyebrow", "Versioned change")}</p>
          <h2>{copy("page.release.versioned.headline", "Change the Workblock, not the platform.")}</h2>
          <p>{copy("page.release.versioned.body", "Each Workblock is versioned, replayed and evaluated on its own. A change ships as a deliberate act with the previous version still in place, so reversal is a decision rather than a project.")}</p>
        </article>
        <article className="apf-ruled-columns__item">
          <p className="apf-ruled-columns__eyebrow">{copy("page.release.isolation.eyebrow", "Isolation")}</p>
          <h2>{copy("page.release.isolation.headline", "Your configuration is yours.")}</h2>
          <p>{copy("page.release.isolation.body", "The platform you run is one exact configuration: your naming, your theme and your content, resolved into a single artifact you can review line by line. Nothing enters it that was not configured for you.")}</p>
        </article>
      </div>

      <section className="apf-statement-plane" aria-labelledby="release-claim">
        <p className="apf-statement-plane__eyebrow">{copy("page.release.claim.eyebrow", "Continuous tailoring")}</p>
        <h2 id="release-claim">{copy("claim.continuous-tailoring")}</h2>
      </section>
      <Trail
        label={copy("trail.label", "Where to go next")}
        items={[
          { to: "/downloads", glyph: "utility.grid", label: copy("trail.downloads"), context: copy("trail.downloads.context") },
          { to: "/reference", glyph: "class.record", label: copy("trail.reference"), context: copy("trail.reference.context") }
        ]}
      />
    </div>
  );
}
